import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import {
    Col,
    IconButton,
    Image,
    Link,
    LinkIcon,
    Row,
    SubHeading,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import getPathSRP from '@/utilities/getPathSRP';

import remoteActions from '@/actions/srp/remoteActions';

import { aiAssistantEntryPointClick } from '@/analytics/aiAssistantEvents';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import authDuck from '@/ducks/authDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import userDuck from '@/ducks/userDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';
import { useSuggestedSearches } from '@/hooks/useSuggestedSearches';

import KeywordSearchContainer from '@/containers/KeywordSearchContainer';
import { KeywordSearchTrigger } from '@/containers/KeywordSearchModalContainer';

import MobileSearchFilterButtons from '@/components/SearchLink/MobileSearchFilterButtons';

import CertifiedTileFilterContainer from './CertifiedTileFilterContainer';
import InventoryClearFilters from './InventoryClearFilters';
import SaveSearchContainer from './SaveSearchContainer';
import SrpHeroContainer from './SrpHeroContainer';
import SuggestedFilterContainer from './SuggestedFilterContainer';

function KeywordButton({ onClick, label = 'Search by keyword' }) {
    return (
        <IconButton
            label={label}
            glyph="ai-search"
            hideLabel
            size="medium"
            onClick={onClick}
        />
    );
}

export function SearchResultsTitleContainer() {
    const dispatch = useDispatch();

    const device = useDevice();
    const lessThanMd = _get(device, 'lessThan.md', false);
    const isXs = _get(device, 'is.xs', false);

    const { locationHeading } = useSelector(srpContentDuck.selectors.getDuckState);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);

    const {
        bookmarkId = null,
        city = '',
        dma = {},
        location = {},
        searchBookmarkId = null,
        sellerType = '',
        state = '',
        zip = '',
    } = useSelector(queryDuck.selectors.getDuckState) || {};

    const savedSearch = useSelector(userDuck.selectors.getSavedSearch) || {};
    const savedSearchTitle = savedSearch.title;
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const navigateToSrp = useSrpNavigation();

    const [backToResultsPath, setPath] = useState('');

    const { publicRuntimeConfig } = getConfig();

    const {
        // feature configs
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        ai_assistant_entry_point: [enableAIAssistantEntryPoint],
        srp_hero: [enableSrpHero, {
            alt_text: altText,
            background_color: backgroundColor,
            cmp: heroCmpId,
            hero_height: heroHeight,
            src: heroSrc,
            logo: logoSrc,
            secondary_link_text: secondaryLinkText,
            hero_card_title: cardTitle,
            hero_card_body: cardBody,
        }],
        srp_now_featuring_cta: [enableNowFeaturing, {
            description,
            cta_label: ctaLabel,
            cta_url: ctaUrl,
        }],
        suggested_keyword: [enableKeyword],
        srp_redesigned_sticky_header: [redesignedStickyHeader],
    } = useFeatures([
        'advanced_keyword_search',
        'ai_assistant_entry_point',
        'compare',
        'enable_private_seller_exchange_logo',
        'srp_hero',
        'srp_now_featuring_cta',
        'personalization',
        'psx_logo',
        'suggested_keyword',
        'srp_redesigned_sticky_header',
    ]);

    const aiEndpoint = `${publicRuntimeConfig?.environment === 'prod' ? 'https://autotrader.com' : 'https://test.autotrader.com'}/ai-mode`;

    useEffect(() => {
        if (searchBookmarkId && isLoggedIn) {
            dispatch(remoteActions.loadSavedSearchBookmark());
        }
        if (bookmarkId) {
            dispatch(remoteActions.handleSearchRedirect(false, navigateToSrp));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchBookmarkId]);

    // sets path for cta in SRP hero
    useEffect(() => {
        const getBackToResultsPath = async () => {
            const path = await getPathSRP({ city, dma, experience: null, location, state, zip }, { basePath: true });
            setPath(path);
        };

        if (enableSrpHero) {
            getBackToResultsPath();
        }
    }, [city, dma, enableSrpHero, location, state, zip]);

    const renderFilterChipsControls = (
        <div className="display-flex flex-nowrap gap-3">
            <SaveSearchContainer
                key="saveSearchContainer"
            />
            <InventoryClearFilters
                key="clearFilters"
            />
        </div>
    );

    const { loadSuggestions = () => {} } = useSuggestedSearches();
    const handleShowKeywordModal = () => {
        dispatch(activeInteractionDuck.creators.setKeys({
            showKeywordSearchModal: true,
        }));
        loadSuggestions();
    };

    const handleAiAssistantClick = async (event) => {
        await sendClick(aiAssistantEntryPointClick, event, {
            placement: 'srp-banner',
            linkText: 'Ask Autotrader',
            url: aiEndpoint,
            cmp: 'text-link::try-our-new-AI-shopping-assistant',
        });
    };

    const heroContent = (
        <SrpHeroContainer
            backToResultsPath={backToResultsPath}
            locationHeading={locationHeading}
            savedSearchTitle={savedSearchTitle}
            sellerType={sellerType}
        />
    );

    const backgroundPosition = useMemo(() => {
        if (heroHeight) {
            return 'center';
        }
        if (lessThanMd) {
            return 'right 10px top 10px';
        }
        return 'center right';
    }, [heroHeight, lessThanMd]);

    return (
        <>
            {/* only display the now featuring or AI assistant banner on non-experience pages */}
            {heroCmpId === 'regular' && (enableNowFeaturing || enableAIAssistantEntryPoint) && (
                <div
                    className="bg-brand-primary text-size-200 text-size-sm-300"
                    data-cmp="srp-now-featuring-banner"
                >
                    <div className="container-lg margin-bottom-2">
                        <div className="display-flex justify-content-center gap-1 margin-vertical-3">
                            {enableAIAssistantEntryPoint ? (
                                <LinkIcon
                                    href={aiEndpoint}
                                    undecorated
                                    target="_blank"
                                    onClick={handleAiAssistantClick}
                                >
                                    Your next car is just a chat away.
                                    <Text weight="bold"> Try AI Mode</Text>
                                </LinkIcon>
                            ) : (
                                <>
                                    {!!description && (
                                        <Text weight="bold">
                                            {description + ' '}
                                        </Text>
                                    )}
                                    <Link
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-bold link-undecorated"
                                        href={ctaUrl}
                                        aria-label={ctaLabel}
                                        undecorated
                                    >
                                        { ctaLabel }
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className={clsx(backgroundColor, { 'container-lg padding-0 padding-horizontal-sm-2': heroHeight })}>
                <div
                    data-cmp={heroCmpId + '-srp-hero'}
                    className={clsx({ 'container-lg': !heroHeight })}
                    style={{
                        position: 'relative',
                        backgroundImage: `url(${heroSrc})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition,
                        backgroundSize: (heroHeight && 'cover') || (lessThanMd ? '100px' : 'auto 90%'),
                        height: heroHeight,
                        marginBottom: '20px',
                    }}

                >
                    {!heroHeight && <div className="padding-vertical-2">{heroContent}</div>}
                    {heroHeight && (
                        <div className="hero-gradient">
                            {heroCmpId === 'hyundai-buy-online' && (
                                <div className="display-flex flex-wrap justify-content-center">
                                    <div className="display-flex justify-content-center align-items-top gap-2 margin-top-3 text-center">
                                        <div>
                                            <Image
                                                src={logoSrc}
                                                alt={altText}
                                                lazyload={false}
                                                loading="eager"
                                                width={140}
                                            />
                                        </div>
                                        <Text
                                            color="inverse"
                                            size={400}
                                            weight="bold"
                                            componentClass="div"
                                        >
                                            Buy Online Store
                                        </Text>
                                    </div>
                                    <Text
                                        className="w-100 text-center"
                                        color="inverse"
                                        size={100}
                                    >
                                        Sponsored by
                                        {' '}
                                        {altText}
                                    </Text>
                                </div>
                            )}

                            {cardBody && (
                                <>
                                    <div className="hero-divider">
                                        <svg
                                            aria-hidden="true"
                                            preserveAspectRatio="none"
                                            viewBox="0 0 1200 120"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{ fill: '#ffffff', width: `${isXs ? '150%' : '125%'}`, height: `${isXs ? '52px' : '75px'}`, transform: 'rotate(180deg) scaleX(-1)' }}
                                        >
                                            <path d="M321.39 56.44c58-10.79 114.16-30.13 172-41.86 82.39-16.72 168.19-17.73 250.45-.39C823.78 31 906.67 72 985.66 92.83c70.05 18.48 146.53 26.09 214.34 3V0H0v27.35a600.21 600.21 0 00321.39 29.09z" />
                                        </svg>
                                    </div>

                                    <div className="header-info-card">
                                        <SubHeading>{cardTitle}</SubHeading>
                                        <p>
                                            {cardBody}
                                            {' '}
                                            <CertifiedTileFilterContainer
                                                filtersValues={filtersValues}
                                                makeCodeKey="makeCode"
                                                uiContext="link"
                                            />
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {secondaryLinkText && isXs && (
                    <span className="pull-right margin-right-3">
                        <Text
                            size={100}
                            className="margin-right-2"
                        >
                            {secondaryLinkText}
                        </Text>
                        <Image
                            src={logoSrc}
                            alt={altText}
                            lazyload={false}
                            loading="eager"
                            width={120}
                        />
                    </span>
                )}
            </div>

            {heroHeight && (
                <div className="container-lg margin-bottom-3">
                    {heroContent}
                </div>
            )}

            {isXs && (
                <div
                    className="container-lg padding-sm-0 margin-vertical-sm-3 margin-bottom-3"
                    style={redesignedStickyHeader ? { borderBottom: '1px solid var(--base-color-neutral-300)' } : null}
                >
                    <Row className="display-flex align-items-center justify-content-between padding-vertical-1 padding-vertical-sm-0">
                        {(!isXs && enableAdvancedKeywordSearch) && (
                            <Col
                                sm={5}
                                md={3}
                            >
                                <KeywordSearchTrigger />
                            </Col>
                        )}
                        {(!isXs && !enableAdvancedKeywordSearch) && (
                            <Col
                                sm={5}
                                md={3}
                            >
                                <KeywordSearchContainer />
                            </Col>
                        )}

                        <Col
                            xs={12}
                            sm={enableKeyword ? 7 : 12}
                            md={enableKeyword ? 9 : 12}
                            className="padding-horizontal-0 padding-horizontal-sm-2"
                        >
                            <div
                                className="display-flex justify-items-center align-items-center flex-direction-row w-100"
                            >
                                {enableAdvancedKeywordSearch && (enableKeyword && isXs && <KeywordButton onClick={handleShowKeywordModal} />)}
                                {redesignedStickyHeader && <MobileSearchFilterButtons showSearchButton={false} />}
                                {!lessThanMd && renderFilterChipsControls}
                                <div
                                    className="padding-top-sm-0"
                                    style={{ minWidth: '100px' }}
                                >
                                    <SuggestedFilterContainer />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );
}

export default SearchResultsTitleContainer;
